import { transformGraphQLFilterToGelly } from "./transformGraphQLFilterToGelly"
import { type FilterStack, type GraphQLFilter } from "./types"

/**
 * Maps operators that don't require values
 */
const NULL_VALUE_OPERATORS = ["isNotEmpty", "isEmpty", "isSet", "isNotSet", "isFalse", "isTrue"] as const

/**
 * Maps operators to their Gelly equivalents
 */
const EQUALITY_OPERATORS = ["==", "=", "is", "equals"] as const
const INEQUALITY_OPERATORS = ["!=", "notEquals"] as const
const CONTAINS_OPERATORS = ["in", "includes"] as const

export const transformFilterStackToGelly = (filterStack: FilterStack | null, extraFilters?: GraphQLFilter | null) => {
  if (!filterStack && !extraFilters) {
    return null
  }

  const parser = new FilterStackToGellyParser()
  const result = parser.parse(filterStack, extraFilters)
  return result ? `[where ${result}]` : null
}

class FilterStackToGellyParser {
  parse(filterStack: FilterStack | null, extraFilters?: GraphQLFilter | null): string | null {
    const stackConditions = filterStack ? this.parseFilterStack(filterStack.filters, filterStack.operator as "and" | "or") : ""
    const extraConditions = extraFilters ? transformGraphQLFilterToGelly(extraFilters, false) : ""
    const allConditions = [stackConditions, extraConditions].filter(Boolean)

    return allConditions.length > 0 ? allConditions.join(" && ") : null
  }

  private parseFilterStack(filters?: FilterStack[], groupOperator: "and" | "or" = "and"): string {
    if (!filters || filters.length === 0) {
      return ""
    }

    const conditions = filters.map((filter) => this.parseFilter(filter)).filter((condition) => condition !== null) as string[]

    if (conditions.length === 0) {
      return ""
    }

    const joinOperator = groupOperator === "or" ? " || " : " && "
    return conditions.join(joinOperator)
  }

  private parseFilter(filter: FilterStack): string | null {
    // Handle logical grouping filters (and/or)
    if (this.isLogicalOperator(filter.operator)) {
      return this.parseLogicalFilter(filter)
    }

    // Handle field comparison filters
    return this.parseComparisonFilter(filter)
  }

  private isLogicalOperator(operator: string): boolean {
    return operator === "and" || operator === "or"
  }

  private parseLogicalFilter(filter: FilterStack): string | null {
    const nestedCondition = this.parseFilterStack(filter.filters, filter.operator as "and" | "or")
    return nestedCondition ? `(${nestedCondition})` : null
  }

  private parseComparisonFilter(filter: FilterStack): string | null {
    if (!filter.operator || !filter.columnId) {
      return null
    }

    return this.buildComparison(filter.operator, filter.columnId, filter.value)
  }

  private buildComparison(operator: string, columnId: string, value?: string | number | boolean): string | null {
    // Handle operators that don't require values
    if (this.isNullValueOperator(operator)) {
      return this.buildNullComparison(operator, columnId)
    }

    // All other operators require a value
    if (value === null || typeof value === "undefined") {
      return null
    }

    return this.buildValueComparison(operator, columnId, value)
  }

  private isNullValueOperator(operator: string): boolean {
    return (NULL_VALUE_OPERATORS as unknown as string[]).includes(operator)
  }

  private buildNullComparison(operator: string, columnId: string): string {
    switch (operator) {
      case "isNotEmpty":
      case "isSet":
        return `${columnId} != null`
      case "isEmpty":
      case "isNotSet":
        return `${columnId} == null`
      case "isFalse":
        return `${columnId} == false`
      case "isTrue":
        return `${columnId} == true`
      default:
        throw new Error(`Unknown null-value operator: ${operator}`)
    }
  }

  private buildValueComparison(operator: string, columnId: string, value: string | number | boolean): string {
    const formattedValue = this.formatValue(value)

    if (this.isEqualityOperator(operator)) {
      return `${columnId} == ${formattedValue}`
    }

    if (this.isInequalityOperator(operator)) {
      return `${columnId} != ${formattedValue}`
    }

    if (this.isContainsOperator(operator)) {
      return this.buildContainsComparison(columnId, value)
    }

    // Default case for other operators (like >=, <=, etc.)
    return `${columnId} ${operator} ${value}`
  }

  private isEqualityOperator(operator: string): boolean {
    return (EQUALITY_OPERATORS as unknown as string[]).includes(operator)
  }

  private isInequalityOperator(operator: string): boolean {
    return (INEQUALITY_OPERATORS as unknown as string[]).includes(operator)
  }

  private isContainsOperator(operator: string): boolean {
    return (CONTAINS_OPERATORS as unknown as string[]).includes(operator)
  }

  private buildContainsComparison(columnId: string, value: string | number | boolean): string {
    if (Array.isArray(value)) {
      const conditions = value.map((val) => `contains(${columnId}, item: "${val}")`)
      return `(${conditions.join(" || ")})`
    }

    return `startsWith(${columnId}, prefix: "${value}")`
  }

  private formatValue(value: string | number | boolean): string {
    return typeof value === "string" ? `"${value}"` : String(value)
  }
}
